import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import { Plus, ExternalLink, AlertOctagon, Loader2, Activity, Clock } from "lucide-react";
import { toast, Toaster } from "sonner";
import { EmptyIncidentsIllustration, EmptyStateCard } from "../components/EmptyStates";

const COMPONENTS = ["api", "payments", "claims", "whatsapp", "uploads", "email"];
const SEVERITIES = ["minor", "major", "critical", "maintenance"];
const STATUSES = ["investigating", "identified", "monitoring", "resolved"];

const SEVERITY_PILL = {
  maintenance: "badge-cooling",
  minor: "badge-pending",
  major: "badge-suspended",
  critical: "badge-rejected",
};

const SEVERITY_COLOR = {
  minor: "#d49a36",
  major: "#c15337",
  critical: "#8b3a23",
  maintenance: "#2c5b61",
};

function fmtMinutes(m) {
  if (m == null) return "—";
  if (m < 60) return `${Math.round(m)}m`;
  if (m < 60 * 24) return `${(m / 60).toFixed(1)}h`;
  return `${(m / 60 / 24).toFixed(1)}d`;
}

export default function IncidentsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState({
    title: "", component: "api", severity: "minor", status: "investigating",
    message: "", scheduled_for: "",
  });

  function load() {
    api.get("/admin/incidents", { params: filter ? { status: filter } : {} })
      .then((r) => setItems(r.data.incidents || []));
    api.get("/admin/incidents/analytics?days=90")
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  }
  useEffect(load, [filter]);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/incidents", {
        ...form,
        scheduled_for: form.scheduled_for || null,
      });
      toast.success("Incident created");
      setOpen(false);
      setForm({ title: "", component: "api", severity: "minor", status: "investigating", message: "", scheduled_for: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  async function update(id, patch) {
    try {
      await api.patch(`/admin/incidents/${id}`, patch);
      toast.success("Incident updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  }

  return (
    <div className="p-8 space-y-6" data-testid="incidents-page">
      <Toaster position="top-right" />
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c15337]">{t("incidents.reliability")}</div>
          <h1 className="font-heading text-3xl font-bold mt-1 text-[#114232]">{t("incidents.title")}</h1>
        </div>
        <div className="flex gap-2">
          <a
            href="/status" target="_blank" rel="noreferrer"
            data-testid="open-public-status"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border border-[#114232]/15 rounded-md bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> {t("incidents.public_page")}
          </a>
          <Button
            onClick={() => setOpen((v) => !v)}
            data-testid="incidents-new-btn"
            className="btn-terracotta rounded-full px-4 hover:bg-[var(--terracotta-hover)]"
          >
            <Plus className="w-4 h-4 mr-1" /> {t("incidents.new_incident")}
          </Button>
        </div>
      </header>

      {/* Analytics */}
      {analytics && (
        <section className="space-y-4" data-testid="incidents-analytics">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="metric-card" data-testid="metric-total-incidents">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">{t("incidents.total_incidents")}</div>
                <Activity className="w-3.5 h-3.5 text-[#5c6360]" />
              </div>
              <div className="mt-3 text-3xl font-mono font-bold text-[#114232]">{analytics.total_incidents}</div>
              <div className="text-xs text-[#5c6360] mt-1">{t("incidents.last_90_days")}</div>
            </div>
            <div className="metric-card" data-testid="metric-open-now" data-flash={analytics.open_incidents > 0 ? "true" : "false"}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">{t("incidents.open_now")}</div>
              <div className="mt-3 text-3xl font-mono font-bold text-[#114232]">{analytics.open_incidents}</div>
            </div>
            <div className="metric-card" data-testid="metric-mttr">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">{t("incidents.mttr")}</div>
                <Clock className="w-3.5 h-3.5 text-[#5c6360]" />
              </div>
              <div className="mt-3 text-3xl font-mono font-bold text-[#114232]">{fmtMinutes(analytics.avg_mttr_minutes)}</div>
            </div>
            <div className="metric-card" data-testid="metric-mtta">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#5c6360] font-semibold">{t("incidents.mtta")}</div>
                <Clock className="w-3.5 h-3.5 text-[#5c6360]" />
              </div>
              <div className="mt-3 text-3xl font-mono font-bold text-[#114232]">{fmtMinutes(analytics.avg_mtta_minutes)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="metric-card" data-testid="chart-by-severity">
              <div className="font-heading text-base font-semibold text-[#114232] mb-2">{t("incidents.by_severity")}</div>
              {analytics.by_severity.length === 0 ? (
                <div className="h-[180px] grid place-items-center text-xs text-[#5c6360]">{t("incidents.no_data")}</div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={analytics.by_severity} dataKey="count" nameKey="severity" cx="50%" cy="50%"
                         innerRadius={36} outerRadius={66} paddingAngle={2}>
                      {analytics.by_severity.map((d) => (
                        <Cell key={d.severity} fill={SEVERITY_COLOR[d.severity] || "#5c6360"} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="metric-card" data-testid="chart-by-component">
              <div className="font-heading text-base font-semibold text-[#114232] mb-2">{t("incidents.by_component")}</div>
              {analytics.by_component.length === 0 ? (
                <div className="h-[180px] grid place-items-center text-xs text-[#5c6360]">{t("incidents.no_data")}</div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={analytics.by_component} layout="vertical" margin={{ left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,66,50,0.08)" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} stroke="#5c6360" fontSize={10} />
                    <YAxis type="category" dataKey="component" stroke="#5c6360" fontSize={11} width={70} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#114232" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="metric-card" data-testid="chart-per-week">
              <div className="font-heading text-base font-semibold text-[#114232] mb-2">{t("incidents.per_week")}</div>
              {analytics.per_week.length === 0 ? (
                <div className="h-[180px] grid place-items-center text-xs text-[#5c6360]">{t("incidents.no_data")}</div>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={analytics.per_week} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(17,66,50,0.08)" vertical={false} />
                    <XAxis dataKey="week" stroke="#5c6360" fontSize={10} />
                    <YAxis stroke="#5c6360" fontSize={10} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid rgba(17,66,50,0.15)", borderRadius: 6, fontSize: 12 }} />
                    <Line type="monotone" dataKey="count" stroke="#c15337" strokeWidth={2} dot={{ r: 3, fill: "#c15337" }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>
      )}

      {open && (
        <form onSubmit={create} className="bg-white border border-[#114232]/10 rounded-lg p-6 space-y-4" data-testid="incident-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">{t("incidents.title_field")}</Label>
              <Input id="title" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="incident-title-input" className="border-[#114232]/15" />
            </div>
            <div className="space-y-2">
              <Label>{t("incidents.component")}</Label>
              <select value={form.component}
                onChange={(e) => setForm({ ...form, component: e.target.value })}
                data-testid="incident-component-select"
                className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm">
                {COMPONENTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t("incidents.severity")}</Label>
              <select value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                data-testid="incident-severity-select"
                className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>{t("incidents.status")}</Label>
              <select value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="incident-status-select"
                className="w-full h-10 px-3 rounded-md border border-[#114232]/15 bg-white text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {form.severity === "maintenance" && (
              <div className="md:col-span-2 space-y-2">
                <Label>{t("incidents.scheduled_for")}</Label>
                <Input type="datetime-local" value={form.scheduled_for}
                  onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                  className="border-[#114232]/15" data-testid="incident-scheduled-input" />
              </div>
            )}
            <div className="md:col-span-2 space-y-2">
              <Label>{t("incidents.opening_note")}</Label>
              <Textarea rows={3} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("incidents.opening_note_placeholder")}
                className="border-[#114232]/15" data-testid="incident-message-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#5c6360]">{t("common.cancel")}</button>
            <Button type="submit" disabled={busy} data-testid="incident-submit-btn"
                    className="btn-baobab rounded-full px-5 hover:bg-[var(--baobab-hover)]">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {t("incidents.create")}
            </Button>
          </div>
        </form>
      )}

      <div className="flex gap-2 text-xs">
        {["", "investigating", "identified", "monitoring", "resolved"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            data-testid={`incident-filter-${s || "all"}`}
            className={`px-3 py-1.5 rounded-full uppercase tracking-wider font-semibold transition-colors ${
              filter === s ? "bg-[#114232] text-[#f4f2eb]" : "bg-white border border-[#114232]/15 text-[#5c6360] hover:text-[#114232]"
            }`}>
            {s || t("incidents.filter_all")}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#114232]/10 rounded-lg overflow-hidden" data-testid="incidents-list">
        {items.length === 0 ? (
          <EmptyStateCard
            testid="incidents-empty"
            illustration={<EmptyIncidentsIllustration className="w-full h-full" />}
            title={t("incidents.empty")}
            body="When something breaks, open an incident so merchants stay informed on the public status page."
          />
        ) : (
          items.map((i) => (
            <div key={i.id} className="border-b border-[#114232]/10 last:border-0 p-5" data-testid={`incident-row-${i.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2 text-[#114232]">
                    {i.severity !== "minor" && i.severity !== "maintenance" && <AlertOctagon className="w-4 h-4 text-[#c15337]" />}
                    {i.title}
                  </div>
                  <div className="text-xs text-[#5c6360] mt-0.5">
                    {i.component} · created {new Date(i.created_at).toLocaleString()}
                    {i.resolved_at && <> · resolved {new Date(i.resolved_at).toLocaleString()}</>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`badge-status ${SEVERITY_PILL[i.severity] || "badge-pending"}`}>{i.severity}</span>
                  <span className="badge-status badge-review">{i.status}</span>
                </div>
              </div>
              {i.updates?.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs">
                  {i.updates.slice(-3).reverse().map((u) => (
                    <li key={u.id} className="border-l-2 border-[#c15337]/40 pl-2.5">
                      <span className="font-semibold uppercase tracking-wider text-[#c15337]">{u.status}</span>
                      <span className="text-[#1b1d1c] ml-2">{u.message}</span>
                    </li>
                  ))}
                </ul>
              )}
              {i.status !== "resolved" && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {STATUSES.filter((s) => s !== i.status).map((s) => (
                    <button key={s}
                      onClick={() => update(i.id, { status: s, message: `Status changed to ${s}` })}
                      data-testid={`incident-${i.id}-set-${s}`}
                      className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md border border-[#114232]/15 hover:bg-[#114232]/5 text-[#5c6360] hover:text-[#114232] transition-colors">
                      → {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
